<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="align-justify" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">My Payslips</h3>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Payslips ({{filteredPayslips.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search payslip" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Payslip Table -->
          <div v-if="!loading && paginatedPayslips.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
              <tr>
                <th scope="col" class="fw-semibold">Payroll Code</th>
                <th scope="col" class="fw-semibold">Month</th>
                <th scope="col" class="fw-semibold">Year</th>
                <th scope="col" class="fw-semibold">Gross</th>
                <th scope="col" class="fw-semibold">Deductions</th>
                <th scope="col" class="fw-semibold">Net</th>
                <th scope="col" class="fw-semibold">Created</th>
                <th scope="col" class="text-end">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="payslip in paginatedPayslips" :key="payslip.id" class="employee-row">
                <td>{{ payslip.payroll.code }}</td>
                <td>{{ payslip.payroll.month }}</td>
                <td>{{ payslip.payroll.year }}</td>
                <td>{{ formatNumber(payslip.gross) }}</td>
                <td>{{ formatNumber(payslip.gross - payslip.net) }}</td>
                <td>{{ formatNumber(payslip.net) }}</td>
                <td>{{ formatDate(payslip.created_at) }}</td>
                <td class="text-end">
                  <button
                      class="btn btn-sm btn-outline-primary"
                      @click="payslipDetails(payslip.id)"
                  >
                    <vue-feather type="eye" size="16"></vue-feather>
                    Details
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && paginatedPayslips.length === 0" class="text-center py-4">
            <div class="empty-state">
              <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                <vue-feather type="users" size="32" stroke="#6c757d"></vue-feather>
              </div>
              <h5>{{ payslips.length === 0 ? 'No Payslips Found' : 'No Matching Payslips Found' }}</h5>
              <p v-if="dataTableSearch" class="text-muted">Try adjusting your search criteria</p>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="!loading && filteredPayslips.length > 0" class="pagination-container d-flex flex-wrap justify-content-around pt-3 pb-4">
            <div class="col-md-3">
              <button class="btn btn-outline-dark text-uppercase shadow-sm" @click="$router.go(-1)">
                <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
                Back
              </button>
            </div>
            <div class="col-md-6 text-center">
              <p class="mb-2 small">Rows per page</p>
              <select class="form-select shadow-sm mx-auto rows-per-page" v-model="dataTablePerPage" data="">
                <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div class="col-md-3">
              <Pagination v-model="dataTablePage" :pages="Math.ceil(filteredPayslips.length/dataTablePerPage)">
                <template v-slot:next>
                  Next
                </template>
                <template v-slot:previous>
                  Prev
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import formatDate from "@/utils/formatDate";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import VueFeather from 'vue-feather';
import payrollService from "@/api/payrollService";
import formatNumber from "../../utils/formatNumber";

export default {
  name: "MyPayslips",
  components: { VueFeather },

  data(){
    return {
      profile: {},
      loading: true,
      payslips: [],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
    }
  },

  computed: {
    filteredPayslips() {
      if (!this.dataTableSearch) {
        return this.payslips;
      }

      const searchTerm = this.dataTableSearch.toLowerCase();
      return this.payslips.filter(payslip => {
        return (
          (payslip.payroll.code && payslip.payroll.code.toLowerCase().includes(searchTerm)) ||
          (payslip.payroll.month && payslip.payroll.month.toLowerCase().includes(searchTerm)) ||
          (payslip.payroll.year && payslip.payroll.year.toString().includes(searchTerm))
        );
      });
    },

    paginatedPayslips() {
      const startIndex = (this.dataTablePage - 1) * this.dataTablePerPage;
      const endIndex = startIndex + this.dataTablePerPage;
      return this.filteredPayslips.slice(startIndex, endIndex);
    },
  },

  methods:{
    formatNumber,
    formatDate,
    async getPayslips() {
      this.loading = true
      try {
        this.payslips = await payrollService.getEmployeePayslips(this.profile.employee_id);
      } catch (error) {
        console.error("Error fetching employees:", error)
      } finally {
        this.loading = false
      }
    },
    payslipDetails(id) {
      router.push('/payslip/' + id)
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getPayslips()
  },

  watch: {
    dataTableSearch() {
      // Reset to first page when search term changes
      this.dataTablePage = 1;
    },
    dataTablePerPage() {
      // Reset to first page when items per page changes
      this.dataTablePage = 1;
    }
  },

}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

.employee-row {
  transition: all 0.2s ease;
}

.employee-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-125 {
  font-size: 1.25rem;
}

.color-gray-600 {
  color: #6c757d;
}

.search-input {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.search-input:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover:not(.disabled) {
  transform: translateY(-2px);
}

.badge {
  padding: 0.5em 0.75em;
}

.rows-per-page {
  width: 80px;
  margin: 0 auto;
}

.rows-per-page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
